import ClientGoogleAnalytics from './ClientGoogleAnalytics';
import TrackingApi from '../api/TrackingApi';
import settings from '../../config/settings';
import _ from 'lodash';
import {CardClickTypes} from './AnalyticsConst';
import {getFingerprintV1, getFingerprintV2, getFingerprints} from '../fingerprint/FingerprintUtils';
import { getTopWindowUrl } from '../utils/DomUtils';
import { redirectActions } from '../const/RedirectConst';

function isEnabled() {
  return !!_.get(settings, 'widget.analytics.internal.enableTracking');
}

function trackPageLoadImpression() {
  return !!_.get(settings, 'widget.analytics.internal.trackPageLoad');
}

const ImpressionTypes = {
  vucard: 'vucard',
  redirectTransition: 'redirect_transition',
};

export default class ClientAnalytics {
  static initialize() {
    try {
      ClientGoogleAnalytics.initialize();
    } catch (e) {
      console.error(e);
    }
  }

  static getFingerprint(cb) {
    getFingerprintV1(cb);
  }

  static getFingerprintV2(cb) {
    getFingerprintV2(cb);
  }

  static sendHostingPageView(url, title, device, publisher) {
    try {
      ClientGoogleAnalytics.sendHostingPageView();
      if (isEnabled() && trackPageLoadImpression()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendPageLoad(fingerprintData, url, title, device, publisher).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendHostingPageViewEvent() {
    try {
      ClientGoogleAnalytics.sendHostingPageViewEvent();
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendCardViewed(mode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId) {
    const referrer = document.referrer || getTopWindowUrl();
    try {
      ClientGoogleAnalytics.sendCardViewed(mode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, referrer);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendImpression(fingerprintData, ImpressionTypes.vucard, mode, rendererId, cardId, campaignId,
            collectionId, showsPageId, referrer).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendCardRedirectTransitionViewed(mode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, destination) {
    const referrer = document.referrer || getTopWindowUrl();
    try {
      ClientGoogleAnalytics.sendCardRedirectTransitionViewed(mode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, referrer, destination);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendImpression(fingerprintData, ImpressionTypes.redirectTransition, mode, rendererId, cardId,
            campaignId, collectionId, showsPageId, referrer, destination).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendCardShared(media, shareUrl, programId, title, rendererId, cardId, campaignId, collectionId) {
    try {
      ClientGoogleAnalytics.sendCardShared(media, shareUrl, programId, title, rendererId, cardId, campaignId, collectionId);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendCardClick(fingerprintData, 'share', media, '', cardId, campaignId, collectionId).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendCardDestinationClicked(name, type, url, programId, title, rendererId, cardId, campaignId, collectionId) {
    const referrer = document.referrer || getTopWindowUrl();
    try {
      ClientGoogleAnalytics.sendCardDestinationClicked(name, url, programId, title, rendererId, cardId, campaignId, collectionId, null, referrer);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendCardClick(fingerprintData, type || CardClickTypes.provider, name, url, cardId, campaignId, collectionId, referrer).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendTriggerDisplayed(renderMode, displayMode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, phrase) {
    const referrer = getTopWindowUrl();
    try {
      ClientGoogleAnalytics.sendTriggerDisplayed(renderMode, displayMode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, phrase, referrer);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendImpression(fingerprintData, renderMode, displayMode, rendererId,
            cardId, campaignId, collectionId, showsPageId, referrer, '', phrase
          ).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendAdReloaded(renderMode, rendererId, campaignId) {
    try {
      ClientGoogleAnalytics.sendAdReloaded(renderMode, rendererId, campaignId);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendImpression(fingerprintData, renderMode, 'reload', rendererId,
            null, campaignId, null, null, '', '', null
          ).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  /**
   * Iframe Ad click tracking
   */
  static sendAdClicked(renderMode, rendererId, campaignId, collectionId, url, title) {
    const referrer = getTopWindowUrl();
    try {
      ClientGoogleAnalytics.sendAdClicked(renderMode, rendererId, campaignId, collectionId, url, title, referrer);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendAdClicked(fingerprintData, renderMode, rendererId, campaignId, collectionId, url, title, referrer).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendTriggered(renderMode, action, displayMode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, phrase) {
    const referrer = getTopWindowUrl();
    try {
      ClientGoogleAnalytics.sendTriggered(renderMode, action, displayMode, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, phrase, referrer);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendRendererClick(fingerprintData, renderMode, rendererId, cardId, campaignId, collectionId, showsPageId, phrase, referrer).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendTriggeredUrl(renderMode, action, url, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, phrase) {
    const referrer = getTopWindowUrl();
    try {
      ClientGoogleAnalytics.sendTriggeredUrl(renderMode, action, url, programId, title, rendererId, cardId, campaignId, collectionId, showsPageId, phrase, referrer);
      if (isEnabled()) {
        getFingerprints((fingerprintData) => {
          TrackingApi.sendRendererClick(fingerprintData, renderMode, rendererId, cardId, campaignId, collectionId, showsPageId, phrase, referrer).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static async sendCardRedirect(cardId, campaignId, action, type, url, device, destination) {
    try {
      const referrer = document.referrer || getTopWindowUrl();
      let gaAction;
      switch (action) {
        default:
          gaAction = action;
          break;
        case redirectActions.auto:
          gaAction = 'auto redirected to destination';
          break;
        case redirectActions.cancel:
          gaAction = 'transition cancel';
          break;
      }

      ClientGoogleAnalytics.sendCardRedirect(cardId, campaignId, gaAction, type, url, device, destination, referrer);
      if (isEnabled()) {
        return new Promise((resolve, reject) => {
          getFingerprints(async (fingerprintData) => {
            try {
              const ret = await TrackingApi.sendRedirect(fingerprintData, cardId, campaignId, action, type, url, device, destination, referrer);
              resolve(ret);
            } catch (e) {
              reject(e);
            }
          });
          resolve();
        });
      }
    } catch (e) {
      return defaultHandleError(e);
    }
  }

  static sendCardFirstLoad(cardId, campaignId, requestMode, destination) {
    const type = 'load';
    try {
      ClientGoogleAnalytics.sendCardRequest(cardId, campaignId, type, requestMode, destination);
      if (isEnabled()) {
        getFingerprints(async (fingerprintData) => {
          TrackingApi.sendCardRequest(fingerprintData, cardId, campaignId, type, requestMode, destination).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendProductPageLoad(publisherId, templateId, productDataSourceId, publisherName, productDataSourceName) {
    const eventCategory = 'Product Page';
    const eventAction = 'load';
    const referrer = getTopWindowUrl();

    try {
      ClientGoogleAnalytics.sendProductPageLoad(eventCategory, eventAction, referrer, publisherId, templateId, productDataSourceId, publisherName, productDataSourceName);
      if (isEnabled()) {
        getFingerprints(async (fingerprintData) => {
          TrackingApi.sendAdEvent(fingerprintData, eventCategory, eventAction, referrer, publisherId, templateId, null, null, productDataSourceId, null, null).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendProductPageProductClick(publisherId, templateId, productDataSourceId, productId, publisherName, productDataSourceName) {
    const eventCategory = 'Product Page - Single Product';
    const eventAction = 'click';
    const referrer = getTopWindowUrl();

    try {
      ClientGoogleAnalytics.sendProductPageProductClick(eventCategory, eventAction, referrer, publisherId, templateId, productDataSourceId, productId, publisherName, productDataSourceName);
      if (isEnabled()) {
        getFingerprints(async (fingerprintData) => {
          TrackingApi.sendAdEvent(fingerprintData, eventCategory, eventAction, referrer, publisherId, templateId, null, null, productDataSourceId, productId, null).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendCommerceUnitEvent(eventAction, publisherId, templateId, campaignId, rendererId, publisherName) {
    const eventCategory = 'Commerce Unit';
    const referrer = getTopWindowUrl();

    try {
      ClientGoogleAnalytics.sendCommerceUnitEvent(eventCategory, eventAction, referrer, publisherId, templateId, campaignId, rendererId, publisherName);
      if (isEnabled()) {
        getFingerprints(async (fingerprintData) => {
          TrackingApi.sendAdEvent(fingerprintData, eventCategory, eventAction, referrer, publisherId, templateId, campaignId, rendererId, null, null, null).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendCommerceUnitProductAdEvent(eventAction, publisherId, templateId, sourceId, productId, campaignId, rendererId, publisherName, sourceName) {
    const eventCategory = 'Commerce Unit - Native Ad';
    const referrer = getTopWindowUrl();

    try {
      ClientGoogleAnalytics.sendCommerceUnitProductAdEvent(eventCategory, eventAction, referrer, publisherId, templateId, sourceId, productId, campaignId, rendererId, publisherName, sourceName);
      if (isEnabled()) {
        getFingerprints(async (fingerprintData) => {
          TrackingApi.sendAdEvent(fingerprintData, eventCategory, eventAction, referrer, publisherId, templateId, campaignId, rendererId, sourceId, productId, null).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }

  static sendCommerceUnitProgrammaticAdEvent(eventAction, publisherId, templateId, sourceId, campaignId, rendererId, publisherName, sourceName) {
    const eventCategory = 'Commerce Unit - Display Ad';
    const referrer = getTopWindowUrl();

    try {
      ClientGoogleAnalytics.sendCommerceUnitProgrammaticAdEvent(eventCategory, eventAction, referrer, publisherId, templateId, sourceId, campaignId, rendererId, publisherName, sourceName);
      if (isEnabled()) {
        getFingerprints(async (fingerprintData) => {
          TrackingApi.sendAdEvent(fingerprintData, eventCategory, eventAction, referrer, publisherId, templateId, campaignId, rendererId, sourceId, null, null).catch(defaultHandleError);
        });
      }
    } catch (e) {
      defaultHandleError(e);
    }
  }
}

function defaultHandleError(e) {
  console.error(e);
}
